import React, { useEffect, useRef, useState } from 'react';
import { X, MapPin, Clock, Navigation2, Users, Loader2, Footprints } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../utils/api';
import { loadGoogleMaps } from '../../utils/googleMaps';

// Per-shift "Visits & time-at-location" panel — turns the breadcrumb trail into:
//  • summary tiles (time at client vs community, stops, road-snapped km)
//  • a visit timeline (each stop: when, how long, client vs community)
//  • a map with the full trail + stop pins + the 400m client geofence circle.
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
const fmtDur = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;

export default function ShiftActivityPanel({ shiftId, title, onClose }) {
  const [data, setData] = useState(null);   // visits response
  const [km, setKm] = useState(null);       // road-snapped km
  const [loading, setLoading] = useState(true);
  const mapEl = useRef(null);
  const leafletRef = useRef(null);   // Leaflet map instance (fallback) — removed on unmount
  const mapArgs = useRef(null);      // { key, path, visits } — rendered by the effect below
  const renderedRef = useRef(false); // render the map only once per open

  useEffect(() => {
    let alive = true;
    setLoading(true);
    renderedRef.current = false;
    Promise.all([
      api.get(`/location/shift/${shiftId}/visits`).then(r => r.data).catch(() => null),
      api.get(`/location/shift/${shiftId}/distance`).then(r => r.data).catch(() => null),
      api.get(`/location/shift/${shiftId}/route`).then(r => r.data).catch(() => null),
      api.get('/maps-config').then(r => r.data?.googleMapsKey || '').catch(() => ''),
    ]).then(([visits, dist, route, key]) => {
      if (!alive) return;
      mapArgs.current = { key, path: route?.path || [], visits };
      setData(visits); setKm(dist); setLoading(false);
    });
    return () => {
      alive = false;
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    };
  }, [shiftId]);

  // Render the map AFTER the container is committed to the DOM (the map <div> only exists once
  // loading is false and there are tracked points) — otherwise mapEl.current is still null.
  useEffect(() => {
    if (loading || renderedRef.current || !mapEl.current || !mapArgs.current) return;
    if (!data?.summary?.points) return;
    renderedRef.current = true;
    renderMap(mapArgs.current.key, mapArgs.current.path, mapArgs.current.visits);
  }, [loading, data]);

  const renderMap = (key, routePath, visits) => {
    if (!mapEl.current || !routePath.length) return;
    // Google engine when a browser key is configured…
    if (key) {
      loadGoogleMaps(key).then((maps) => {
        if (!mapEl.current) return;
        const path = routePath.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }));
        const map = new maps.Map(mapEl.current, { zoom: 13, mapTypeControl: false, streetViewControl: false, fullscreenControl: false });
        new maps.Polyline({ path, strokeColor: '#6366f1', strokeOpacity: 0.85, strokeWeight: 4, map });
        const b = new maps.LatLngBounds();
        path.forEach(p => b.extend(p));
        (visits?.visits || []).forEach((v, i) => {
          new maps.Marker({ position: { lat: v.lat, lng: v.lng }, map,
            label: { text: String(i + 1), color: '#fff', fontSize: '11px', fontWeight: '700' },
            icon: { path: maps.SymbolPath.CIRCLE, scale: 11, fillColor: v.type === 'client' ? '#059669' : '#d97706', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 } });
          b.extend({ lat: v.lat, lng: v.lng });
        });
        if (visits?.client) {
          new maps.Circle({ center: { lat: visits.client.lat, lng: visits.client.lng }, radius: 400, map,
            fillColor: '#6366f1', fillOpacity: 0.08, strokeColor: '#6366f1', strokeOpacity: 0.4, strokeWeight: 1 });
          b.extend({ lat: visits.client.lat, lng: visits.client.lng });
        }
        map.fitBounds(b, 40);
      }).catch(() => renderLeaflet(routePath, visits));   // key bad/blocked → fall back
      return;
    }
    // …otherwise free Leaflet tiles (no key needed) so the route always shows.
    renderLeaflet(routePath, visits);
  };

  const renderLeaflet = (routePath, visits) => {
    if (!mapEl.current || !routePath.length) return;
    if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    const path = routePath.map(p => [parseFloat(p.lat), parseFloat(p.lng)]).filter(p => !Number.isNaN(p[0]));
    if (path.length < 2) return;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false });
    leafletRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors', subdomains: 'abc' }).addTo(map);
    L.polyline(path, { color: '#6366f1', weight: 4, opacity: 0.85 }).addTo(map);
    const bounds = L.latLngBounds(path);
    (visits?.visits || []).forEach((v, i) => {
      L.circleMarker([v.lat, v.lng], { radius: 9, fillColor: v.type === 'client' ? '#059669' : '#d97706', fillOpacity: 1, color: '#fff', weight: 2 })
        .addTo(map).bindTooltip(String(i + 1), { permanent: true, direction: 'center', className: 'stop-num' });
      bounds.extend([v.lat, v.lng]);
    });
    if (visits?.client) {
      L.circle([visits.client.lat, visits.client.lng], { radius: 400, color: '#6366f1', weight: 1, opacity: 0.4, fillColor: '#6366f1', fillOpacity: 0.08 }).addTo(map);
      bounds.extend([visits.client.lat, visits.client.lng]);
    }
    map.fitBounds(bounds, { padding: [40, 40] });
    // Container is sized by CSS (h-64) after init — nudge Leaflet to recompute.
    setTimeout(() => { if (leafletRef.current) leafletRef.current.invalidateSize(); }, 100);
  };

  const s = data?.summary;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl z-10 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Navigation2 size={18} className="text-primary-500" />
            <h2 className="text-base font-bold text-slate-900">Visits & time at location{title ? ` — ${title}` : ''}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={28} /></div>
        ) : !data || !data.summary?.points ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3 text-center px-6">
            <MapPin size={26} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No tracking data for this shift yet</p>
            <p className="text-xs text-slate-400">Visits appear once the worker clocks in with the latest app and a GPS trail is recorded.</p>
          </div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-5">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tile icon={Users} label="At client" value={fmtDur(s.clientMinutes)} tone="emerald" />
              <Tile icon={MapPin} label="Community" value={fmtDur(s.communityMinutes)} tone="amber" />
              <Tile icon={Clock} label="Stops" value={s.stops} tone="slate" />
              <Tile icon={Footprints} label="Distance" value={`${km?.km ?? s.rawTravelKm} km`} tone="indigo" hint={km?.snapped ? 'road-snapped' : 'approx'} />
            </div>

            {/* Map */}
            <div ref={mapEl} className="w-full h-64 rounded-xl border border-slate-200 bg-slate-50" />

            {/* Visit timeline */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Timeline</p>
              {(!data.visits || !data.visits.length) ? (
                <p className="text-sm text-slate-400">No stops detected (worker was moving the whole shift).</p>
              ) : (
                <div className="space-y-2">
                  {data.visits.map((v, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/60">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${v.type === 'client' ? 'bg-emerald-500' : 'bg-amber-500'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{v.label}</p>
                        <p className="text-xs text-slate-400">{fmtTime(v.enteredAt)} – {fmtTime(v.exitedAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-700">{fmtDur(v.durationMin)}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${v.type === 'client' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{v.type === 'client' ? 'Client' : 'Community'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone, hint }) {
  const tones = { emerald: 'text-emerald-600 bg-emerald-50', amber: 'text-amber-600 bg-amber-50', slate: 'text-slate-600 bg-slate-100', indigo: 'text-indigo-600 bg-indigo-50' };
  return (
    <div className="rounded-xl border border-slate-100 p-3">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-2 ${tones[tone]}`}><Icon size={15} /></div>
      <p className="text-lg font-bold text-slate-800 leading-none">{value}</p>
      <p className="text-[11px] text-slate-400 mt-1">{label}{hint ? ` · ${hint}` : ''}</p>
    </div>
  );
}
